/* =============================================
 * 検索結果ページ「新着順」
 * 標準の検索は新着順に非対応のため、新着順が選ばれたときだけ
 * Storefront API（トークン不要）で同じ条件の検索結果を全件取得し、
 * 公開日の新しい順に並べ替えて表示する。
 * 商品カードは /products/{handle}?section_id=search-new-arrival-card で
 * テーマの product-card をそのまま取得する。
 * ============================================= */
(() => {
  const SORT_VALUE = 'created-descending';
  const API_VERSION = '2026-07';
  const CACHE_TTL = 5 * 60 * 1000;
  const config = document.getElementById('SearchNewArrivalsConfig');
  if (!config) return;

  const perPage = parseInt(config.dataset.perPage, 10) || 12;
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const language = ((window.Shopify && window.Shopify.locale) || 'ja').split('-')[0].toUpperCase();
  const country = ((window.Shopify && window.Shopify.country) || 'JP').toUpperCase();

  const QUERY = `query($q: String!, $after: String, $filters: [ProductFilter!], $prefix: SearchPrefixQueryType)
    @inContext(language: ${language}, country: ${country}) {
    search(query: $q, first: 250, after: $after, types: [PRODUCT], prefix: $prefix,
      unavailableProducts: LAST, productFilters: $filters) {
      pageInfo { hasNextPage endCursor }
      nodes { ... on Product { handle createdAt availableForSale } }
    }
  }`;

  let running = 0;

  const params = () => new URLSearchParams(window.location.search);
  const isActive = () => params().get('sort_by') === SORT_VALUE;

  // URL の filter.* を Storefront API の ProductFilter に変換する
  function buildFilters(p) {
    const filters = [];
    const price = {};
    for (const [key, raw] of p.entries()) {
      if (!key.startsWith('filter.') || raw === '') continue;
      const value = raw;
      if (key === 'filter.v.price.gte') price.min = parseFloat(value.replace(/,/g, ''));
      else if (key === 'filter.v.price.lte') price.max = parseFloat(value.replace(/,/g, ''));
      else if (key === 'filter.v.availability') filters.push({ available: value === '1' });
      else if (key === 'filter.p.product_type') filters.push({ productType: value });
      else if (key === 'filter.p.vendor') filters.push({ productVendor: value });
      else if (key === 'filter.p.tag') filters.push({ tag: value });
      else if (key.startsWith('filter.p.m.')) {
        const [namespace, ...rest] = key.slice('filter.p.m.'.length).split('.');
        filters.push({ productMetafield: { namespace, key: rest.join('.'), value } });
      } else if (key.startsWith('filter.v.m.')) {
        const [namespace, ...rest] = key.slice('filter.v.m.'.length).split('.');
        filters.push({ variantMetafield: { namespace, key: rest.join('.'), value } });
      } else if (key.startsWith('filter.v.option.')) {
        filters.push({ variantOption: { name: key.slice('filter.v.option.'.length), value } });
      }
    }
    if (price.min !== undefined || price.max !== undefined) filters.push({ price });
    return filters;
  }

  async function fetchAll(q, filters, prefix) {
    const cacheKey = 'sna:' + JSON.stringify([language, q, filters, prefix]);
    try {
      const cached = JSON.parse(sessionStorage.getItem(cacheKey));
      if (cached && Date.now() - cached.t < CACHE_TTL) return cached.items;
    } catch (e) { /* storage unavailable */ }

    const items = [];
    let after = null;
    do {
      const res = await fetch(`${root}api/${API_VERSION}/graphql.json`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: QUERY, variables: { q, after, filters, prefix } })
      });
      const json = await res.json();
      if (json.errors || !json.data) throw new Error('search api error');
      const s = json.data.search;
      s.nodes.forEach((n) => n && n.handle && items.push(n));
      after = s.pageInfo.hasNextPage ? s.pageInfo.endCursor : null;
    } while (after);

    // 標準の検索と同じく売り切れは後ろ、その中で公開日の新しい順
    items.sort((a, b) =>
      (b.availableForSale - a.availableForSale) || (Date.parse(b.createdAt) - Date.parse(a.createdAt)));
    const slim = items.map((n) => n.handle);
    try { sessionStorage.setItem(cacheKey, JSON.stringify({ t: Date.now(), items: slim })); } catch (e) { /* ignore */ }
    return slim;
  }

  async function fetchCard(handle) {
    const res = await fetch(`${root}products/${encodeURIComponent(handle)}?section_id=search-new-arrival-card`);
    if (!res.ok) return '';
    const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
    const section = doc.querySelector('.shopify-section') || doc.body;
    return section.innerHTML.trim();
  }

  function pageUrl(n) {
    const p = params();
    p.set('page', n);
    return `${window.location.pathname}?${p.toString()}`;
  }

  function renderPagination(grid, current, total) {
    const pages = Math.ceil(total / perPage);
    let wrap = grid.parentElement.querySelector('pagination-theme');
    if (pages <= 1) { if (wrap) wrap.remove(); return; }
    if (!wrap) {
      wrap = document.createElement('pagination-theme');
      wrap.className = 'pagination pagination-type--paginated';
      wrap.innerHTML = '<div class="row"><div class="small-12 columns"><div class="page-numbers nav-links"></div></div></div>';
      grid.insertAdjacentElement('afterend', wrap);
    }
    // URL 由来の値は href / textContent でのみ扱う（innerHTML に入れない）
    const icon = (id, fallback) => {
      const tpl = document.getElementById(id);
      return tpl ? tpl.content.cloneNode(true) : document.createTextNode(fallback);
    };
    const span = (cls, n, child) => {
      const s = document.createElement('span');
      s.className = cls;
      if (n === null) {
        s.appendChild(child);
      } else {
        const a = document.createElement('a');
        a.href = pageUrl(n);
        a.appendChild(child);
        s.appendChild(a);
      }
      return s;
    };
    const nav = wrap.querySelector('.page-numbers');
    nav.replaceChildren();
    const add = (el) => { nav.appendChild(el); nav.appendChild(document.createTextNode(' ')); };
    if (current > 1) add(span('prev', current - 1, icon('SearchNewArrivalsPrev', '‹')));
    let gap = false;
    for (let i = 1; i <= pages; i++) {
      if (i === 1 || i === pages || Math.abs(i - current) <= 2) {
        add(i === current ? span('page current', null, document.createTextNode(String(i))) : span('page', i, document.createTextNode(String(i))));
        gap = false;
      } else if (!gap) {
        add(span('deco', null, document.createTextNode('…')));
        gap = true;
      }
    }
    if (current < pages) add(span('next', current + 1, icon('SearchNewArrivalsNext', '›')));
  }

  function selectOption() {
    document.querySelectorAll('select[name="sort_by"]').forEach((select) => {
      if ([...select.options].some((o) => o.value === SORT_VALUE)) select.value = SORT_VALUE;
    });
  }

  async function apply() {
    if (!isActive()) return;
    const grid = document.getElementById('product-grid');
    if (!grid) return;
    const run = ++running;
    selectOption();
    const collection = grid.closest('.collection') || grid;
    collection.classList.add('loading');
    grid.style.visibility = 'hidden';

    try {
      const p = params();
      const prefix = (p.get('options[prefix]') || 'last').toUpperCase() === 'NONE' ? 'NONE' : 'LAST';
      const handles = await fetchAll(p.get('q') || '', buildFilters(p), prefix);
      const current = Math.max(1, parseInt(p.get('page'), 10) || 1);
      const pageHandles = handles.slice((current - 1) * perPage, current * perPage);
      const cards = await Promise.all(pageHandles.map(fetchCard));
      if (run !== running) return;
      grid.innerHTML = cards.filter(Boolean).map((html) => `<li class="column">${html}</li>`).join('');
      renderPagination(grid, current, handles.length);
    } catch (e) {
      // 失敗時は標準の並び（関連性）のまま表示する
      console.warn('[search-new-arrivals]', e);
    } finally {
      if (run === running) {
        grid.style.visibility = '';
        collection.classList.remove('loading');
      }
    }
  }

  // 絞り込み・並び替えの変更でグリッドが差し替わったら再適用する
  const container = document.getElementById('ProductGridContainer');
  if (container) {
    new MutationObserver((mutations) => {
      if (mutations.some((m) => [...m.addedNodes].some((n) => n.nodeType === 1 && (n.id === 'product-grid' || (n.querySelector && n.querySelector('#product-grid')))))) apply();
    }).observe(container, { childList: true, subtree: true });
  }
  window.addEventListener('popstate', () => setTimeout(apply, 0));
  apply();
})();
